import {
  CarbonMeasurementSystem,
  CarbonUnit,
  ConvertUnits,
  getUnitsForSystem,
  isUnitNumberType,
  MeasurementSystem,
  UnitNumberType,
} from "../../utils/units/units";
import { CycleSlot } from "../../utils/metrics";
import { isNil } from "../../utils/identity";

type NumericUnitSettings = {
  decimalPlaces: number;
  imperial: DefaultUnits;
  metric: DefaultUnits;
};

type DefaultUnits = {
  default: ConvertUnits;
  units: ConvertUnits[];
};

const unitsDefault = (
  units: ConvertUnits[],
  _default = units[0]!
): DefaultUnits => ({
  default: _default,
  units,
});

const unitSettings = (
  decimalPlaces: number,
  imperial: DefaultUnits,
  metric = imperial
): NumericUnitSettings => ({
  decimalPlaces,
  imperial,
  metric,
});

/**
 * @param decimalPlaces how many decimal places should be shown for this cycleSlot
 * @param imperial the default imperial unit
 * @param metric the default metric unit
 *
 * @returns Settings where the options for imperial units are all the units that
 * the default imperial unit can be converted to, and likewise for metric
 **/
const getSettingsForDefaults = (
  decimalPlaces: number,
  imperial: ConvertUnits,
  metric: ConvertUnits
): NumericUnitSettings =>
  unitSettings(
    decimalPlaces,
    unitsDefault(
      getUnitsForSystem(imperial, MeasurementSystem.imperial),
      imperial
    ),
    unitsDefault(getUnitsForSystem(metric, MeasurementSystem.metric), metric)
  );

/**
 * maps a CycleSlot to settings for that cycle, including available units and decimal places to be used
 **/
export const NUMERIC_UNITS_BY_KIND: Record<CycleSlot, NumericUnitSettings> = {
  [CycleSlot.ALTITUDE]: getSettingsForDefaults(2, "ft", "m"),
  [CycleSlot.AREA]: getSettingsForDefaults(2, "ac", "ha"),
  [CycleSlot.COMPONENT_ENCODERS]: unitSettings(
    2,
    unitsDefault(["in"]),
    unitsDefault(["in", "cm"])
  ),
  [CycleSlot.COVERAGE]: getSettingsForDefaults(2, "ac/h", "ha/h"),
  [CycleSlot.DENSITY]: getSettingsForDefaults(2, "/ft2", "/m2"),
  [CycleSlot.DISTANCE]: getSettingsForDefaults(2, "mi", "km"),
  [CycleSlot.LIFETIME]: unitSettings(0, unitsDefault(["h"])),
  [CycleSlot.SHOOT]: unitSettings(0, unitsDefault(["ms"])),
  [CycleSlot.SIZE]: unitSettings(
    2,
    unitsDefault(["in", "mm"], "mm"),
    unitsDefault(["mm"])
  ),
  [CycleSlot.SPEED]: getSettingsForDefaults(2, "mph", "km/h"),
  [CycleSlot.STATUS_AREA]: getSettingsForDefaults(0, "ac", "ha"),
  [CycleSlot.STATUS_TIME]: getSettingsForDefaults(0, "h", "h"),
  [CycleSlot.TIME]: getSettingsForDefaults(2, "h", "h"),
  [CycleSlot.TIME_LIMITED]: unitSettings(2, unitsDefault(["h", "min", "s"])),
};

/**
 * @returns the number of decimal places that should be used for the given cycleSlot and unit
 **/
export const getDecimalPlacesForCycleSlot = (
  cycleSlot: CycleSlot | undefined,
  unit: CarbonUnit | undefined
): number =>
  unit === UnitNumberType.PERCENT
    ? 1
    : isNil(cycleSlot) || isNil(unit)
    ? 0
    : NUMERIC_UNITS_BY_KIND[cycleSlot].decimalPlaces;

/**
 * @returns either the user's saved unit or the default unit for the given cycleSlot
 * and measurementSystem, as well as available units for the cycleSlot.
 **/
export const getSavedUnit = (
  measurementSystem: CarbonMeasurementSystem,
  cycleSlots: Record<string, CarbonUnit>,
  cycleSlot: CycleSlot | undefined,
  unit: CarbonUnit
): { units?: CarbonUnit[]; unit: CarbonUnit } => {
  if (isNil(cycleSlot) || isUnitNumberType(unit)) {
    return { units: undefined, unit };
  }
  const unitMeta = NUMERIC_UNITS_BY_KIND[cycleSlot][measurementSystem];
  // Non-ConvertUnits CarbonUnits are Count and Percent, which don't have cycles, and
  // should not be in cycleSlots
  const saved = cycleSlots[cycleSlot] as ConvertUnits;
  if (isNil(saved)) {
    return { units: unitMeta.units, unit: unitMeta.default };
  }
  return {
    units: unitMeta.units,
    unit:
      isNil(saved) || !unitMeta.units.includes(saved as ConvertUnits)
        ? unitMeta.default
        : saved,
  };
};
